import React from "react";

const HVACStartupChecklist = () => {
  return (
    <div className="min-h-screen bg-black text-white px-4 py-2">

      {/* Header */}
      <header className="mb-10">
        <h1 className="text-lg lg:text-2xl font-bold flex items-center gap-3">
          <i className="fa-solid fa-list-check text-cyan-400"></i>
          HVAC Startup Checklist – India
        </h1>
        <p className="mt-3 text-xs lg:text-sm text-white/80 leading-relaxed">
          End-to-end operational, compliance, financial, and sustainability
          checklist for building a future-ready HVAC enterprise.
        </p>
      </header>

      {/* 1. Foundational Clarity */}
      <section className="mb-10">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-4 text-yellow-200">
          <i className="fa-solid fa-brain text-purple-400"></i>
          1. Foundational Clarity (Do This First)
        </h2>

        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>❑ Business vision & long-term intent</li>
          <li>❑ Residential / Commercial / Industrial / DC / Healthcare</li>
          <li>❑ Installation / Service / AMC / Retrofit / IoT / HVACaaS</li>
          <li>❑ Local / Regional / Pan-India strategy</li>
          <li>❑ Energy-efficient positioning</li>
          <li>❑ Compliance-first (BEE / ECBC)</li>
          <li>❑ Certified technicians & SOP-driven ops</li>
          <li>❑ Digital monitoring / IoT readiness</li>
          <li>❑ Sustainability score & ESG readiness</li>
        </ul>
      </section>

      {/* 2. Market Validation */}
      <section className="mb-10">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-4 text-yellow-200">
          <i className="fa-solid fa-chart-line text-green-400"></i>
          2. Market & Opportunity Validation
        </h2>

        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>❑ City & zone demand mapping</li>
          <li>❑ Peak season & capacity analysis</li>
          <li>❑ Ticket size (₹ per project / AMC)</li>
          <li>❑ Homeowners, builders, MSMEs, hospitals, schools</li>
          <li>❑ Facility management companies</li>
          <li>❑ Competition: local techs, OEMs, aggregators</li>
          <li>❑ Pricing vs quality gap identification</li>
        </ul>
      </section>

      {/* 3. Legal Setup */}
      <section className="mb-10">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-4 text-yellow-200">
          <i className="fa-solid fa-building-columns text-yellow-400"></i>
          3. Legal & Business Setup
        </h2>

        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>❑ Proprietorship / Partnership / LLP / Pvt Ltd</li>
          <li>❑ PAN, GST, Udyam (MSME)</li>
          <li>❑ Shop & Establishment License</li>
          <li>❑ Bank current account</li>
          <li>❑ Startup India (optional but strong)</li>
          <li>❑ Trademark registration</li>
          <li>❑ ISO 9001 / 14001 (scaling phase)</li>
        </ul>
      </section>

      {/* 4. Compliance & Safety */}
      <section className="mb-10">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-4 text-yellow-200">
          <i className="fa-solid fa-shield-halved text-red-400"></i>
          4. Compliance & Safety
        </h2>

        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>❑ BEE, ECBC, ECO Niwas awareness</li>
          <li>❑ Energy labeling understanding</li>
          <li>❑ Low-GWP refrigerant readiness (R-32)</li>
          <li>❑ Refrigerant handling & recovery SOP</li>
          <li>❑ Electrical & A2L safety SOPs</li>
          <li>❑ PPE, fire & work-at-height safety</li>
          <li>❑ Workmen comp & liability insurance</li>
        </ul>
      </section>

      {/* 5. Tools */}
      <section className="mb-10">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-4 text-yellow-200">
          <i className="fa-solid fa-toolbox text-orange-400"></i>
          5. Tools, Equipment & Infrastructure
        </h2>

        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>❑ Vacuum pump, gauges, nitrogen cylinder</li>
          <li>❑ Leak detector & recovery machine</li>
          <li>❑ Power tools & electrical testers</li>
          <li>❑ PPE kits & safety gear</li>
          <li>❑ Service vehicle or logistics tie-up</li>
          <li>❑ Spares & inventory tracking</li>
        </ul>
      </section>

      {/* 6. Workforce */}
      <section className="mb-10">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-4 text-yellow-200">
          <i className="fa-solid fa-people-group text-blue-400"></i>
          6. Workforce & Skills
        </h2>

        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>❑ Lead HVAC engineer / supervisor</li>
          <li>❑ Installation & service technicians</li>
          <li>❑ Helper / apprentice pipeline</li>
          <li>❑ ITI / NSDC certifications</li>
          <li>❑ Refrigerant & energy efficiency training</li>
          <li>❑ Continuous learning (VRF, IoT, AI)</li>
          <li>❑ Root cause thinking (Think5Whys)</li>
        </ul>
      </section>

      {/* 7. Digital Ops */}
      <section className="mb-10">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-4 text-yellow-200">
          <i className="fa-solid fa-laptop text-cyan-400"></i>
          7. Digital & Operations Setup
        </h2>

        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>❑ CRM & lead tracking</li>
          <li>❑ Job scheduling & dispatch</li>
          <li>❑ GST billing & invoicing</li>
          <li>❑ AMC lifecycle management</li>
          <li>❑ Installation & service logs</li>
          <li>❑ Refrigerant & energy data logs</li>
        </ul>
      </section>

      {/* 8–13 condensed */}
      <section className="mb-10">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-4 text-yellow-200">
          <i className="fa-solid fa-road text-emerald-400"></i>
          8–13. Growth, Sustainability & Scale
        </h2>

        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>❑ Strong branding & trust positioning</li>
          <li>❑ AMC-first recurring revenue model</li>
          <li>❑ Clean financial hygiene & cash flow</li>
          <li>❑ HVACaaS, retrofit & analytics</li>
          <li>❑ Sustainability documentation & ESG</li>
          <li>❑ Scale via cities, OEMs, PSU contracts</li>
          <li>❑ Avoid: price-only play & poor docs</li>
        </ul>
      </section>

      {/* Final Note */}
      <footer className="border-t border-gray-800 pt-5">
        <p className="text-xs text-center text-white">
          HVAC is no longer just a service business — it is
          <strong> energy + climate + skills + trust infrastructure</strong>.
        </p>
      </footer>

    </div>
  );
};

export default HVACStartupChecklist;
